/* eslint-disable no-unused-expressions */
import type { ReactElement } from 'react';
import {
  InputContainer,
  InputText,
  Accordion,
  Icon,
  FileInput,
  Tooltip,
} from '@betrybe/ada-react';
import {
  color,
  cpfMask,
  hasIcon,
  issuingBodies,
  textColor,
  ufList,
} from '@/utils';
import { SelectInput } from '@/components/molecules';

type DocumentsFormTypes = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: any;
  handleFileUpload: (
    event: { file: File }[],
    field: string,
    fieldName: string,
  ) => void;
  cpfFileRef: React.LegacyRef<HTMLDivElement>;
  rgFileRef: React.LegacyRef<HTMLDivElement>;
  selfieWithDocumentRef: React.LegacyRef<HTMLDivElement>;
  apiError?: string;
  setApiError?: React.Dispatch<React.SetStateAction<string>>;
};

function DocumentsForm(props: DocumentsFormTypes): ReactElement {
  const {
    formik,
    handleFileUpload,
    cpfFileRef,
    rgFileRef,
    selfieWithDocumentRef,
    apiError,
    setApiError,
  } = props;

  const accordionError: boolean =
    (formik.touched.cpf && formik.errors.cpf) ||
    (formik.touched.cpfFile && formik.errors.cpfFile) ||
    (formik.touched.rg && formik.errors.rg) ||
    (formik.touched.rg_issuing_body && formik.errors.rg_issuing_body) ||
    (formik.touched.rg_issuing_state && formik.errors.rg_issuing_state) ||
    (formik.touched.rgFile && formik.errors.rgFile) ||
    (formik.touched.selfieWithDocument && formik.errors.selfieWithDocument) ||
    apiError === 'cpf' ||
    apiError === 'rg';
  const accordionChecked: boolean =
    !accordionError &&
    formik.values.cpf &&
    formik.values.cpfFile &&
    formik.values.rg &&
    formik.values.rg_issuing_body &&
    formik.values.rg_issuing_state &&
    formik.values.rgFile &&
    formik.values.selfieWithDocument;

  const textHeader = (
    <div className="flex flex-row space-x-2 items-center">
      {hasIcon({ accordionChecked, accordionError })}
      <p
        className={`bold text-base ${textColor({
          accordionChecked,
          accordionError,
        })}`}
      >
        Documentos
      </p>
    </div>
  );

  return (
    <Accordion
      header={textHeader}
      markerColor={color({ accordionChecked, accordionError })}
    >
      <div className="flex flex-col m-6 space-y-5">
        <InputContainer
          label="CPF"
          error={formik.touched.cpf && formik.errors.cpf}
        >
          {(inputProps) => (
            <div>
              <div>
                <InputText
                  {...inputProps}
                  id="input-register-cpf"
                  type="text"
                  name="cpf"
                  placeholder="000.000.000-00"
                  onChange={(e) => {
                    const value = cpfMask(e.target.value);
                    formik.setFieldValue('cpf', value);
                    setApiError && setApiError('');
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.cpf}
                  data-testid="cpf"
                />
              </div>
            </div>
          )}
        </InputContainer>
        <InputContainer
          label=""
          error={formik.touched.cpfFile && formik.errors.cpfFile}
        >
          {(inputProps) => (
            <div ref={cpfFileRef}>
              <div>
                <FileInput
                  {...inputProps}
                  buttonLabel="Adicionar arquivo"
                  id="cpfFile"
                  name="cpfFile"
                  helpText={
                    <>
                      Você pode obter junto ao RG ou CNH, ou no{' '}
                      <a
                        href="https://servicos.receita.fazenda.gov.br/Servicos/CPF/ConsultaSituacao/ConsultaPublica.asp"
                        className="underline text-royal-blue-500"
                        target="_blank"
                        rel="noreferrer"
                      >
                        site da Receita
                      </a>
                      . Arquivos de até 5Mb. Formatos: imagem .jpg ou documento
                      .pdf
                    </>
                  }
                  label="CPF digitalizado"
                  multiple={false}
                  onChange={async (e) => {
                    await handleFileUpload(e, 'cpfFile', 'cpf');
                  }}
                  accept=".jpg,.jpeg,.pdf"
                  items={formik.values.cpfFile && [formik.values.cpfFile]}
                  onDelete={() => formik.setFieldValue('cpfFile', null)}
                  data-testid="cpfFile"
                />
              </div>
            </div>
          )}
        </InputContainer>

        <InputContainer
          label="RG"
          error={formik.touched.rg && formik.errors.rg}
        >
          {(inputProps) => (
            <div>
              <div>
                <InputText
                  {...inputProps}
                  id="input-register-rg"
                  type="text"
                  name="rg"
                  placeholder="00.000.000-0"
                  onChange={(e) => {
                    formik.setFieldValue('rg', e.target.value);
                    setApiError && setApiError('');
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.rg}
                  data-testid="rg"
                />
              </div>
            </div>
          )}
        </InputContainer>
        <div className="register-container__issuer-row">
          <InputContainer
            label="Órgão emissor"
            error={
              formik.touched.rg_issuing_body && formik.errors.rg_issuing_body
            }
            className="register-container__issuer-row--input-width"
          >
            {(inputProps) => (
              <div>
                <div>
                  <SelectInput
                    {...inputProps}
                    id="issuerBody"
                    onChange={formik.handleChange}
                    placeholder="ssp"
                    value={formik.values.rg_issuing_body}
                    name="rg_issuing_body"
                    options={issuingBodies.map((issuerBody) => ({
                      label: issuerBody.label,
                      value: issuerBody.value,
                    }))}
                    errorMessage=""
                    data-testid="issuerBody"
                  />
                </div>
              </div>
            )}
          </InputContainer>
          <InputContainer
            label="Estado emissor"
            error={
              formik.touched.rg_issuing_state && formik.errors.rg_issuing_state
            }
            className="register-container__issuer-row--input-width"
          >
            {(inputProps) => (
              <div>
                <div>
                  <SelectInput
                    {...inputProps}
                    id="issuerState"
                    onChange={formik.handleChange}
                    placeholder="SP"
                    value={formik.values.rg_issuing_state}
                    name="rg_issuing_state"
                    options={ufList.map((issuerState) => ({
                      label: issuerState,
                      value: issuerState,
                    }))}
                    errorMessage=""
                    data-testid="issuerState"
                  />
                </div>
              </div>
            )}
          </InputContainer>
        </div>
        <InputContainer
          label=""
          error={formik.touched.rgFile && formik.errors.rgFile}
        >
          {(inputProps) => (
            <div ref={rgFileRef}>
              <div>
                <FileInput
                  {...inputProps}
                  buttonLabel="Adicionar arquivo"
                  id="rgFile"
                  name="rgFile"
                  helpText="São válidos RG, CNH, Passaporte, Carteira de Trabalho. Formatos: .jpg ou .pdf até 5Mb"
                  label="Documento de identificação digitalizado"
                  multiple={false}
                  onChange={async (e) => {
                    await handleFileUpload(e, 'rgFile', 'rg');
                  }}
                  accept=".jpg,.jpeg,.pdf"
                  items={formik.values.rgFile && [formik.values.rgFile]}
                  onDelete={() => formik.setFieldValue('rgFile', null)}
                  data-testid="rgFile"
                />
              </div>
            </div>
          )}
        </InputContainer>
        <InputContainer
          label=""
          error={
            formik.touched.selfieWithDocument &&
            formik.errors.selfieWithDocument
          }
        >
          {(inputProps) => (
            <div ref={selfieWithDocumentRef}>
              <div className="register-container__selfie-row">
                <FileInput
                  {...inputProps}
                  buttonLabel="Adicionar arquivo"
                  id="selfieWithDocument"
                  name="selfieWithDocument"
                  helpText="Foto com documento de identidade. Arquivos de até 5Mb. Formatos: imagem .jpg ou documento .pdf"
                  label={
                    <div className="register-container__file-input-label">
                      <>Selfie com o documento</>
                      <Tooltip
                        title="Aqui vão 4 dicas para uma boa selfie com documento
                        (1) Aceitamos os documentos: RG, CNH, Passaporte e Carteira de Trabalho
                        (2) Tire a foto em um ambiente bem iluminado
                        (3) Segure o documento do lado que mostra sua imagem, próximo ao seu rosto, como na ilustração de exemplo
                        (4) Confira se seu rosto e documento estão nítidos – não podem ficar cobertos nem mesmo censurados"
                        backgroundColor="black-800"
                      >
                        <Icon
                          name="help"
                          className="register-container__tooltip-icon"
                          width={18}
                        />
                      </Tooltip>
                    </div>
                  }
                  multiple={false}
                  onChange={async (e) => {
                    await handleFileUpload(e, 'selfieWithDocument', 'selfie');
                  }}
                  accept=".jpg,.jpeg,.pdf"
                  items={
                    formik.values.selfieWithDocument && [
                      formik.values.selfieWithDocument,
                    ]
                  }
                  onDelete={() =>
                    formik.setFieldValue('selfieWithDocument', null)
                  }
                  data-testid="selfieWithDocument"
                />
                <img
                  src="\assets\images\person-with-document.svg"
                  alt="pessoa com documento"
                />
              </div>
            </div>
          )}
        </InputContainer>
      </div>
    </Accordion>
  );
}

DocumentsForm.defaultProps = {
  apiError: '',
  setApiError: undefined,
};

export default DocumentsForm;
